
<template>
  <div class="data-safe-api-page">
    <section class="hero-section">
      <div class="container hero-grid">
        <div class="hero-text">
          <h1 class="hero-title">数据保险箱SDK</h1>
          <p class="hero-desc">
            数据保险箱SDK是对传统安全范式的革新。它创造性地引入“数据细胞”理念，将三维加密、DNA标识与血缘追踪等安全基因内置于数据本身，使之成为具备自主安全与治理能力的活性单元。这确保了数据在任何流转环节中均可控、可溯。产品以轻量SDK形式提供，能快速融入企业现有系统，为数据资产化提供内生安全的核心支撑。
          </p>
        </div>
        <div class="hero-visual">
          <img
            class="hero-image"
            alt="Data Security Illustration"
            src="@/assets/sdk/sdk.png"
          />
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container">
        <div class="section-head">
          <h2 class="section-title">核心功能</h2>
        </div>
        <div class="feature-grid">
          <div
            v-for="item in coreFeatures"
            :key="item.title"
            class="feature-card"
            :style="{ '--overlay': item.overlay }"
          >
            <img class="feature-bg" :alt="item.alt" :src="item.image" />
            <div class="feature-overlay"></div>
            <div class="feature-content">
              <h3 class="feature-title">{{ item.title }}</h3>
              <p class="feature-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container">
        <div class="section-head">
          <h2 class="section-title">产品优势</h2>
        </div>

        <div class="adv-grid">
          <div v-for="item in advantages" :key="item.title" class="glass-card adv-card">
            <div class="adv-icon">
              <i :class="item.icon" />
            </div>
            <div class="adv-body">
              <h3 class="adv-title">{{ item.title }}</h3>
              <div class="adv-desc" v-if="item.desc">
                {{ item.desc }}
              </div>
              <ul class="adv-list" v-if="item.list && item.list.length">
                <li v-for="line in item.list" :key="line">{{ line }}</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="adv-wide-wrap">
          <div class="glass-card adv-card adv-wide">
            <div class="adv-icon">
              <i class="el-icon-time" />
            </div>
            <div class="adv-body">
              <h3 class="adv-title">面向数据资产化的长期价值</h3>
              <div class="adv-desc">
                数据保险箱SDK 不仅解决当下的数据安全合规问题，更为：
              </div>
              <ul class="adv-list">
                <li>数据资产管理</li>
                <li>数据流通与共享</li>
                <li>数据要素市场化</li>
              </ul>
              <div class="adv-desc">提供统一、安全、可扩展的底层能力支撑。</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section quick-start-section">
      <div class="container">
        <div class="section-head">
          <h2 class="section-title">快速开始</h2>
          <p class="section-subtitle">
            数据保险箱SDK 采用传统软件开发工具包形式，支持集成至企业现有业务系统与数据平台中。无需调整原有系统架构，支持渐进式部署与分阶段接入。
          </p>
        </div>

        <div class="steps">
          <div v-for="(step, index) in quickSteps" :key="step" class="step-item">
            <div class="glass-step">{{ step }}</div>
            <div v-if="index < quickSteps.length - 1" class="step-arrow">
              <i class="el-icon-right" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container">
        <div class="section-head">
          <h2 class="section-title">面向用户</h2>
        </div>
        <div class="audience-grid">
          <div
            v-for="item in audiences"
            :key="item.title"
            class="audience-card"
            :style="{ '--audienceOverlay': item.overlay }"
          >
            <img class="audience-bg" :alt="item.alt" :src="item.image" />
            <div class="audience-overlay"></div>
            <div class="audience-title">{{ item.title }}</div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "DataSafeApi",
  data() {
    return {
      coreFeatures: [
        {
          title: "数据三维加密与解密",
          desc: "数据保险箱SDK 提供统一的数据加密与解密接口，采用区别于传统“平面加密”的三维加密设计思想，将数据内容、结构特征与上下文信息纳入加密体系。",
          image:
            require("@/assets/sdk/sdk1.png"),
          alt: "Data Encryption Abstract",
          overlay: "rgba(30, 58, 138, 0.62)",
        },
        {
          title: "数据确权",
          desc: "数据保险箱SDK 为数据引入明确的权属模型，通过确权接口为数据绑定权属信息，支持所有权、管理权、使用权等多维权利关系的表达。",
          image:
            require("@/assets/sdk/sdk2.png"),
          alt: "Electronic chip tree pattern",
          overlay: "rgba(30, 64, 175, 0.68)",
        },
        {
          title: "数据 DNA 获取",
          desc: "数据保险箱SDK 可为每一份数据生成唯一的数据 DNA 标识。数据 DNA 是数据的“身份指纹”，用于唯一标识数据本体及其衍生关系。为数据确权、血缘追踪与审计提供统一标识基础。",
          image:
            require("@/assets/sdk/sdk3.png"),
          alt: "DNA Strand",
          overlay: "rgba(49, 46, 129, 0.6)",
        },
        {
          title: "数据血缘获取与溯源",
          desc: "数据保险箱SDK 支持记录数据在生成、复制、加工、分发和使用过程中的血缘关系，构建完整的数据流转路径。支持事后审计、风险定位与责任追溯，满足监管合规与内部审计需求。",
          image:
            require("@/assets/sdk/sdk4.png"),
          alt: "Network lineage pattern",
          overlay: "rgba(12, 74, 110, 0.68)",
        },
      ],
      advantages: [
        {
          icon: "el-icon-lock",
          title: "数据“自带安全”，不是依赖外围防护",
          desc: "区别于传统加密工具，只保护数据内容，数据保险箱SDK将安全能力、权属信息与流转记忆内嵌于数据本体。即使数据脱离原业务系统，仍然具备安全与控制能力。",
        },
        {
          icon: "el-icon-warning-outline",
          title: "三维加密 + 数据 DNA，安全与识别一体化",
          list: ["安全保护不再是单一维度", "数据身份与安全能力高度绑定", "加密、确权与溯源形成闭环"],
        },
        {
          icon: "el-icon-finished",
          title: "确权与血缘机制，天然支撑合规与审计",
          desc: "数据保险箱SDK在数据产生与流转过程中自动建立权属与血缘关系，而非事后补录。使合规审计从“被动追溯”升级为“过程留痕”。",
        },
        {
          icon: "el-icon-setting",
          title: "工程化设计，低侵入、易集成",
          list: ["不改变原有系统架构", "可按模块、按场景逐步接入", "适合存量系统与新系统并行部署"],
        },
      ],
      quickSteps: ["引入 数据保险箱SDK", "初始配置", "调用接口", "业务系统对接"],
      audiences: [
        {
          title: "金融",
          image:
            require("@/assets/sdk/sdk5.png"),
          alt: "Finance Environment",
          overlay: "linear-gradient(to top, rgba(30, 58, 138, 0.92), rgba(59, 130, 246, 0.18))",
        },
        {
          title: "医疗",
          image:
            require("@/assets/sdk/sdk6.png"),
          alt: "Medical Environment",
          overlay: "linear-gradient(to top, rgba(22, 78, 99, 0.92), rgba(6, 182, 212, 0.18))",
        },
        {
          title: "能源与互联网",
          image:
            require("@/assets/sdk/sdk7.png"),
          alt: "Energy IoT Environment",
          overlay: "linear-gradient(to top, rgba(12, 74, 110, 0.92), rgba(14, 165, 233, 0.18))",
        },
        {
          title: "科研与高端制造",
          image:
            require("@/assets/sdk/sdk8.png"),
          alt: "High Tech Manufacturing",
          overlay: "linear-gradient(to top, rgba(49, 46, 129, 0.92), rgba(99, 102, 241, 0.18))",
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
// @import url("https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@300;400;500;700&display=swap");

.data-safe-api-page {
  position: relative;
  min-height: 100vh;
  color: #1e293b;
  font-family: "Noto Sans SC", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif;
}

.data-safe-api-page::before {
  content: "";
  position: fixed;
  inset: 0;
  background-color: #f0f9ff;
  background-image: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  background-size: cover;
  background-attachment: fixed;
  z-index: -1;
}

.container {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
}

.hero-section {
  padding: 80px 0;
  background: #eff5fd;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;
}

.hero-title {
  font-size: 40px;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 24px;
}

.hero-desc {
  margin: 0;
  font-size: 18px;
  line-height: 1.75;
  color: #475569;
}

.hero-visual {
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 20px 30px rgba(2, 132, 199, 0.18));
}

.section {
  padding: 80px 0;
}

.section-head {
  text-align: center;
  margin-bottom: 56px;
}

.section-title {
  font-size: 40px;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.section-subtitle {
  max-width: 980px;
  margin: 24px auto 0;
  font-size: 18px;
  line-height: 1.75;
  color: #475569;
}

.feature-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

.feature-card {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  height: 260px;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.18);
}

.feature-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1);
  transition: transform 0.5s ease;
}

.feature-card:hover .feature-bg {
  transform: scale(1.08);
}

.feature-overlay {
  position: absolute;
  inset: 0;
  background: var(--overlay);
}

.feature-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 32px;
}

.feature-title {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px;
  text-align: left;
}

.feature-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.92);
  text-align: left;
}

.glass-card {
  background: rgba(255, 255, 255, 0.62);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.64);
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.12);
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease,
    border-color 0.3s ease;
}

.glass-card:hover {
  transform: translateY(-4px);
  background: rgba(255, 255, 255, 0.78);
  border-color: rgba(59, 130, 246, 0.3);
  box-shadow: 0 12px 26px rgba(59, 130, 246, 0.16);
}

.adv-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

.adv-card {
  border-radius: 16px;
  padding: 28px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.adv-icon {
  width: 80px;
  height: 80px;
  border-radius: 999px;
  background: rgba(219, 234, 254, 0.45);
  border: 1px solid rgba(191, 219, 254, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.adv-icon i {
  font-size: 36px;
  color: #3b82f6;
}

.adv-title {
  margin: 0 0 12px;
  font-size: 20px;
  line-height: 1.3;
  font-weight: 700;
  color: #0f172a;
}

.adv-desc {
  font-size: 13px;
  line-height: 1.7;
  color: #475569;
}

.adv-list {
  padding-left: 18px;
  margin: 12px 0 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
}

.adv-list li + li {
  margin-top: 4px;
}

.adv-wide-wrap {
  margin-top: 28px;
}

.adv-wide {
  max-width: 980px;
  margin: 0 auto;
}

.quick-start-section {
  overflow: hidden;
}

.steps {
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: stretch;
  justify-content: center;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.glass-step {
  width: 100%;
  border-radius: 14px;
  padding: 18px 20px;
  text-align: center;
  font-weight: 700;
  color: #0f172a;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.44);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  transition: transform 0.25s ease;
}

.glass-step:hover {
  transform: translateY(-3px);
}

.step-arrow {
  display: none;
  color: rgba(59, 130, 246, 0.6);
  flex: 0 0 auto;
}

.step-arrow i {
  font-size: 34px;
}

.audience-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.audience-card {
  position: relative;
  height: 260px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.14);
  cursor: pointer;
  transform: translateY(0);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.audience-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 34px rgba(15, 23, 42, 0.18);
}

.audience-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1);
  transition: transform 0.5s ease;
}

.audience-card:hover .audience-bg {
  transform: scale(1.08);
}

.audience-overlay {
  position: absolute;
  inset: 0;
  background: var(--audienceOverlay);
}

.audience-title {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 18px;
  text-align: center;
  font-size: 22px;
  letter-spacing: 0.04em;
  font-weight: 800;
  color: #ffffff;
}

@media (min-width: 768px) {
  .container {
    padding: 0 24px;
  }

  .hero-visual {
    height: 320px;
  }

  .feature-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .adv-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .steps {
    flex-direction: row;
    gap: 0;
    align-items: center;
  }

  .step-item {
    flex: 0 0 auto;
  }

  .glass-step {
    width: 260px;
  }

  .step-arrow {
    display: block;
    margin: 0 10px;
  }

  .audience-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .container {
    padding: 0 32px;
  }

  .hero-grid {
    grid-template-columns: 1.1fr 0.9fr;
  }

  .hero-visual {
    height: 420px;
  }

  .audience-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
